import React, { useEffect, useState, forwardRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Container from '../Container';
import EmptyButton from '../EmptyButton';
import ClickOutside from '../ClickOutside';
import Ghost from './Ghost';
import Text from '../Text';

const StyledButton = styled(EmptyButton)`
  transition: all ease 0.3s;
  &:hover,
  &:focus {
    background: ${props => props.theme.background.background300};
  }
`;

const AutoSuggest = forwardRef(
  ({ data, render, onSelect, itemDisplayText, itemKey, ...props }, ref) => {
    const [show, setShow] = useState(!!data);

    useEffect(() => {
      setShow(!!data);
    }, [data]);

    const onKeyDown = (e, item) => {
      e.preventDefault();

      const elm = e.currentTarget;
      const nextElm = elm.nextElementSibling;
      const prevElm = elm.previousElementSibling;

      if (e.keyCode === 27) {
        setShow(false);
      }
      if (e.keyCode === 40 && nextElm) {
        nextElm.focus();
      }
      if (e.keyCode === 38 && prevElm) {
        prevElm.focus();
      }
      if (e.keyCode === 13) {
        onSelect(item);
      }
    };

    const defaultRender = item => (
      <StyledButton
        key={item[itemKey || itemDisplayText]}
        textAlign="left"
        fontSize="16px"
        color="normal"
        padding="13px 10px"
        width="100%"
        onClick={() => onSelect(item)}
        onKeyDown={e => onKeyDown(e, item)}
      >
        {item[itemDisplayText]}
      </StyledButton>
    );

    if (!show) return null;

    return (
      <ClickOutside
        position="absolute"
        width="100%"
        onClickOutside={() => setShow(false)}
        zIndex="5"
      >
        <Container
          className="lsCommonClientAutoSuggest"
          borderRadius="11px"
          border="1px solid"
          borderColor={theme => theme.primary.normal}
          overflow="auto"
          width="100%"
          backgroundColor="white"
          maxHeight="200px"
          ref={ref}
          autoComplete="chrome-off"
          {...props}
        >
          {data && data.length ? (
            data.map(item => (render ? render(item) : defaultRender(item)))
          ) : (
            <Container
              display="flex"
              flexDirection="column"
              alignItems="center"
              padding
            >
              <Ghost />
              <Text fontWeight="600">No results match your search</Text>
              <Text
                fontSize="small"
                textAlign="center"
                color="text300"
                marginTop
              >
                Try a different spelling or
                <br />
                contacting head office
              </Text>
            </Container>
          )}
        </Container>
      </ClickOutside>
    );
  }
);

AutoSuggest.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  render: PropTypes.func,
  itemDisplayText: PropTypes.string,
  itemKey: PropTypes.string,
  onSelect: PropTypes.func,
};

AutoSuggest.defaultProps = {
  data: null,
  render: null,
  itemDisplayText: 'text',
  itemKey: null,
  onSelect: () => {},
};

export default AutoSuggest;
